const trackingUtils = require('od-modules/utils/tracking.utils');

import BaseView from 'components-framework/view/base.view';
import {
    OFFER_TYPES,
    SELECTORS,
    EVENTS,
    TRACKING,
    OFFER_BENEFITS_KEYS_INIT_SUBSTR,
    TRACKING_LABEL,
    CLASSNAMES,
    OFFER_ZERO,
    SIGN_PLUS,
} from './constants';

const isPrimeWidgetStepOpenFirstTime = () => Odigeo.Utils.parseJson(Odigeo.Session.get('isPrimeWidgetStepOpenFirstTime'));

const isReactivateOffer = (offerType) => offerType === OFFER_TYPES.REACTIVATE;

const View = BaseView.extend({
    events: EVENTS,
    selectors: SELECTORS,
    render() {
        this.updatedKeys = this.updateKeys();
        const wrapperTemplate = this.buildWrapperTemplate({
            errorMessageComponent: this.buildErrorMessageTemplate(),
            headerComponent: this.buildHeaderTemplate(),
            footerComponent: this.buildFooterTemplate(),
            offerComponents: Object.values(OFFER_TYPES).map((offerType) =>
                this.buildOfferTemplate({
                    offerType,
                })
            ),
        });
        this.$el.html(wrapperTemplate);
        this.toggleShowMembershipRecaptureWidget();
    },

    updateKeys() {
        const {headerSubtitle1, terms, termsLink, termsLinkText, confirmationPositiveTitle, confirmationInformativeTitle} = this.keys;
        return {
            ...this.keys,
            confirmationPositiveTitle: Odigeo.Utils.replaceCmsKeyArgs(confirmationPositiveTitle, this.model.get('membershipExpirationDate')),
            confirmationInformativeTitle: Odigeo.Utils.replaceCmsKeyArgs(confirmationInformativeTitle, this.model.get('membershipExpirationDate')),
            headerSubtitle1: Odigeo.Utils.replaceCmsKeyArgs(headerSubtitle1, this.model.get('membershipExpirationDate')),
            terms: Odigeo.Utils.replaceCmsKeyArgs(terms, this.buildTermsLink(termsLink, termsLinkText)),
        };
    },

    buildWrapperTemplate({errorMessageComponent, headerComponent, offerComponents, footerComponent, confirmationComponent}) {
        return this.templates.wrapper({
            errorMessageComponent,
            headerComponent,
            offerComponents,
            footerComponent,
            confirmationComponent,
        });
    },

    buildErrorMessageTemplate() {
        const {selectionWarningMessage} = this.updatedKeys;
        return this.templates.errorMessage({selectionWarningMessage});
    },

    buildHeaderTemplate() {
        const {headerTitle, headerSubtitle1} = this.updatedKeys;
        return this.templates.header({
            headerTitle,
            headerSubtitle: this.model.get('membershipExpirationDate') && headerSubtitle1,
        });
    },

    buildFooterTemplate() {
        let {terms} = this.updatedKeys;
        return this.templates.footer({
            terms,
        });
    },

    buildTermsLink(termsLink, termsLinkText) {
        return `<a class="odf-link-legal odf-link-sm" href="${termsLink}" target="_blank">${termsLinkText}</a>`;
    },

    buildOfferTemplate({offerType}) {
        return this.templates.offer({
            headerComponent: this.buildOfferHeaderTemplate({
                offerType,
            }),
            benefitsComponent: this.buildOfferBenefitsComponent({offerType}),
            offerType,
        });
    },

    buildOfferHeaderTemplate({offerType}) {
        const {offerActivateMembershipTitle, offerNoActivateMembershipTitle, offerActivateMembershipPill} = this.updatedKeys;
        return this.templates.offerHeader({
            offerHeaderTitle: isReactivateOffer(offerType) ? offerActivateMembershipTitle : offerNoActivateMembershipTitle,
            offerPillText: isReactivateOffer(offerType) && offerActivateMembershipPill,
            discountLost: `${SIGN_PLUS} ${Odigeo.Utils.formatCurrency(OFFER_ZERO)}`,
            offerType,
        });
    },

    buildOfferBenefitsComponent({offerType}) {
        return this.templates.offerBenefits({
            isReactivateOffer: isReactivateOffer(offerType),
            benefits: isReactivateOffer(offerType)
                ? this.getOfferBenefitsKeys(OFFER_BENEFITS_KEYS_INIT_SUBSTR.REACTIVATE)
                : this.getOfferBenefitsKeys(OFFER_BENEFITS_KEYS_INIT_SUBSTR.NO_REACTIVATE),
        });
    },

    getOfferBenefitsKeys(offerBenefitsKeyInitSubstr) {
        return Object.entries(this.updatedKeys).reduce((acc, key) => {
            const [keyName, keyValue] = key;
            if (keyName.startsWith(offerBenefitsKeyInitSubstr)) {
                acc = [...acc, keyValue];
            }
            return acc;
        }, []);
    },

    onChangeOffer(e) {
        const offerSelected = e && $(e.currentTarget).data('offer');
        const isReactivateOfferSelected = isReactivateOffer(offerSelected);
        this.model.set({
            isReactivateOfferSelected,
            isReactivateDecisionTaken: true,
        });

        this.tracking(isReactivateOfferSelected ? TRACKING_LABEL.ON_CHANGE_OFFER_REACTIVATE : TRACKING_LABEL.ON_CHANGE_OFFER_NOREACTIVATE);
        this.togglePendingMembershipReactivateDecisionMessage();
        this.executeChangeOffer(isReactivateOfferSelected);
    },

    toggleShowMembershipRecaptureWidget() {
        this.toggleHiddenClassMembershipRecaptureWidget();
        isPrimeWidgetStepOpenFirstTime() && this.tracking(TRACKING_LABEL.ON_LOAD);
    },

    toggleHiddenClassMembershipRecaptureWidget() {
        const isPrimeWidgetStepAlreadyOpened = isPrimeWidgetStepOpenFirstTime();
        this.$el.toggleClass(CLASSNAMES.ODF_HIDDEN, !isPrimeWidgetStepAlreadyOpened);
    },

    togglePendingMembershipReactivateDecisionMessage() {
        const isReactivateDecisionTaken = this.model.get('isReactivateDecisionTaken');
        this.$el
            .find(this.selectors.ERROR_PENDING_REACTIVATE_DECISION_MESSAGE_CONTAINER)
            .toggleClass(CLASSNAMES.ODF_HIDDEN, isReactivateDecisionTaken);
        if (!isReactivateDecisionTaken) {
            this.tracking(TRACKING_LABEL.ON_ERROR);
            Odigeo.Utils.scrollToElement(this.$el.find(this.selectors.ERROR_PENDING_REACTIVATE_DECISION_MESSAGE_CONTAINER));
        }
        return !isReactivateDecisionTaken;
    },

    executeChangeOffer(isReactivateOfferSelected) {
        this.model.set('isReactivateOfferSelected', isReactivateOfferSelected);
    },

    triggerTrackingOnPayment() {
        const isReactivateOfferSelected = this.model.get('isReactivateOfferSelected');
        this.tracking(isReactivateOfferSelected ? TRACKING_LABEL.ON_PAYMENT_REACTIVATE : TRACKING_LABEL.ON_PAYMENT_NOREACTIVATE);
    },

    triggerTrackingOnContinue() {
        const isReactivateOfferSelected = this.model.get('isReactivateOfferSelected');
        this.tracking(isReactivateOfferSelected ? TRACKING_LABEL.ON_CONTINUE_REACTIVATE : TRACKING_LABEL.ON_CONTINUE_NOREACTIVATE);
    },

    renderConfirmationComponent() {
        const wrapperTemplate = this.buildWrapperTemplate({
            confirmationComponent: this.buildConfirmationTemplate(),
        });
        this.$el.html(wrapperTemplate);
    },

    buildConfirmationTemplate() {
        const isReactivateOfferSelected = this.model.get('isReactivateOfferSelected');
        const {
            confirmationPositiveTitle,
            confirmationInformativeTitle,
            confirmationPositiveSubtitle,
            confirmationInformativeSubtitle,
        } = this.updatedKeys;
        return this.templates.confirmation(
            isReactivateOfferSelected
                ? {confirmationTitle: confirmationPositiveTitle, confirmationSubtitle: confirmationPositiveSubtitle, isReactivateOfferSelected}
                : {confirmationTitle: confirmationInformativeTitle, confirmationSubtitle: confirmationInformativeSubtitle, isReactivateOfferSelected}
        );
    },

    tracking(label) {
        trackingUtils.trackEventWithCategory(TRACKING.CATEGORY, TRACKING.ACTION, label);
    },
});

export default View;
